import { inject, Injectable, Provider } from '@angular/core';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
	MatDateFormats,
} from '@angular/material/core';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { CUSTOM_DATE_FORMAT } from '../constants/common.constant';

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

/** Same formats as MAT_MOMENT_DATE_FORMATS. */
export const DAYJS_DATE_FORMATS: MatDateFormats = {
	parse: {
		dateInput: 'l',
		timeInput: 'LT',
	},
	display: {
		dateInput: 'l',
		timeInput: 'LT',
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
		timeOptionLabel: 'LT',
	},
};

interface LocaleNames {
	firstDayOfWeek: number;
	longMonths: string[];
	shortMonths: string[];
	dates: string[];
	longDaysOfWeek: string[];
	shortDaysOfWeek: string[];
	narrowDaysOfWeek: string[];
}

const ISO_8601 = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** Accept unpadded months and days ("2026-1-5") like Moment's lenient parsing. */
function loosen(format: string): string {
	return format.replace(/(?<![MD])(MM|DD)(?![MD])/g, (token) => token[0]);
}

/** Numbers in a date string, ignoring zero padding. */
function numbersIn(value: string): number[] {
	return (value.match(/\d+/g) ?? []).map(Number);
}

/** DateAdapter for the Material datepicker backed by dayjs, in local time. */
@Injectable()
export class DayjsDateAdapter extends DateAdapter<Dayjs> {
	private names!: LocaleNames;

	/** Loaded dayjs locale for `locale` ("en-US" resolves to "en"). */
	private dayjsLocale = dayjs.locale();

	constructor() {
		super();
		this.setLocale((inject(MAT_DATE_LOCALE, { optional: true }) as string | null) || dayjs.locale());
	}

	override setLocale(locale: string): void {
		super.setLocale(locale);
		this.dayjsLocale = dayjs().locale(locale).locale();
		const data = dayjs().locale(this.dayjsLocale).localeData();
		this.names = {
			firstDayOfWeek: data.firstDayOfWeek(),
			longMonths: data.months(),
			shortMonths: data.monthsShort(),
			dates: Array.from({ length: 31 }, (_, i) => String(i + 1)),
			longDaysOfWeek: data.weekdays(),
			shortDaysOfWeek: data.weekdaysShort(),
			narrowDaysOfWeek: data.weekdaysMin(),
		};
	}

	getYear(date: Dayjs): number {
		return date.year();
	}

	getMonth(date: Dayjs): number {
		return date.month();
	}

	getDate(date: Dayjs): number {
		return date.date();
	}

	getDayOfWeek(date: Dayjs): number {
		return date.day();
	}

	getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
		return style === 'long' ? this.names.longMonths : this.names.shortMonths;
	}

	getDateNames(): string[] {
		return this.names.dates;
	}

	getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
		if (style === 'long') return this.names.longDaysOfWeek;
		if (style === 'short') return this.names.shortDaysOfWeek;
		return this.names.narrowDaysOfWeek;
	}

	getYearName(date: Dayjs): string {
		return this.clone(date).format('YYYY');
	}

	getFirstDayOfWeek(): number {
		return this.names.firstDayOfWeek;
	}

	getNumDaysInMonth(date: Dayjs): number {
		return date.daysInMonth();
	}

	clone(date: Dayjs): Dayjs {
		return date.clone().locale(this.dayjsLocale);
	}

	createDate(year: number, month: number, date: number): Dayjs {
		if (month < 0 || month > 11) {
			throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
		}
		if (date < 1) {
			throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
		}

		// setFullYear keeps years 0-99 as-is; the Date constructor maps them to 19xx.
		const native = new Date(2000, 0, 1);
		native.setFullYear(year, month, date);
		native.setHours(0, 0, 0, 0);

		if (native.getMonth() !== month) {
			throw Error(`Invalid date "${date}" for month with index "${month}".`);
		}
		return dayjs(native).locale(this.dayjsLocale);
	}

	today(): Dayjs {
		return dayjs().locale(this.dayjsLocale);
	}

	parse(value: unknown, parseFormat: string | string[]): Dayjs | null {
		if (value && typeof value === 'string') {
			return this.parseString(value, parseFormat);
		}
		return value ? dayjs(value as dayjs.ConfigType).locale(this.dayjsLocale) : null;
	}

	format(date: Dayjs, displayFormat: string): string {
		if (!this.isValid(date)) {
			throw Error('DayjsDateAdapter: Cannot format invalid date.');
		}
		return this.clone(date).format(displayFormat);
	}

	addCalendarYears(date: Dayjs, years: number): Dayjs {
		return this.clone(date).add(years, 'year');
	}

	addCalendarMonths(date: Dayjs, months: number): Dayjs {
		return this.clone(date).add(months, 'month');
	}

	addCalendarDays(date: Dayjs, days: number): Dayjs {
		return this.clone(date).add(days, 'day');
	}

	toIso8601(date: Dayjs): string {
		return this.clone(date).format();
	}

	override deserialize(value: unknown): Dayjs | null {
		if (value instanceof Date) {
			const date = dayjs(value).locale(this.dayjsLocale);
			return date.isValid() ? date : this.invalid();
		}
		if (this.isDateInstance(value)) {
			return this.clone(value);
		}
		if (typeof value === 'string') {
			if (!value) return null;
			if (ISO_8601.test(value)) {
				const date = dayjs(value).locale(this.dayjsLocale);
				if (date.isValid()) return date;
			}
		}
		return super.deserialize(value);
	}

	isDateInstance(obj: unknown): obj is Dayjs {
		return dayjs.isDayjs(obj);
	}

	isValid(date: Dayjs): boolean {
		return date.isValid();
	}

	invalid(): Dayjs {
		return dayjs(NaN);
	}

	override setTime(target: Dayjs, hours: number, minutes: number, seconds: number): Dayjs {
		if (hours < 0 || hours > 23) {
			throw Error(`Invalid hours "${hours}". Hours value must be between 0 and 23.`);
		}
		if (minutes < 0 || minutes > 59) {
			throw Error(`Invalid minutes "${minutes}". Minutes value must be between 0 and 59.`);
		}
		if (seconds < 0 || seconds > 59) {
			throw Error(`Invalid seconds "${seconds}". Seconds value must be between 0 and 59.`);
		}
		return this.clone(target).hour(hours).minute(minutes).second(seconds).millisecond(0);
	}

	override getHours(date: Dayjs): number {
		return date.hour();
	}

	override getMinutes(date: Dayjs): number {
		return date.minute();
	}

	override getSeconds(date: Dayjs): number {
		return date.second();
	}

	override parseTime(value: unknown, parseFormat: string | string[]): Dayjs | null {
		return this.parse(value, parseFormat);
	}

	override addSeconds(date: Dayjs, amount: number): Dayjs {
		return this.clone(date).add(amount, 'second');
	}

	/**
	 * Lenient like Moment: padding and separators may differ from the format, but
	 * out-of-range parts ("2026-13-01") are invalid instead of rolling over.
	 */
	private parseString(value: string, parseFormat: string | string[]): Dayjs {
		const formats = Array.isArray(parseFormat) ? parseFormat : [parseFormat];
		for (const format of formats) {
			const date = dayjs(value, loosen(format), this.dayjsLocale);
			if (!date.isValid()) continue;

			const expected = numbersIn(value);
			const actual = numbersIn(date.format(format));
			if (expected.length === actual.length && expected.every((n, i) => n === actual[i])) {
				return date;
			}
		}
		return this.invalid();
	}
}

/** Providers for a component's datepickers; replaces provideMomentDateAdapter. */
export function provideDayjsDateAdapter(
	formats: MatDateFormats = CUSTOM_DATE_FORMAT
): Provider[] {
	return [
		{ provide: DateAdapter, useClass: DayjsDateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: formats },
	];
}
